import React from 'react';
import { Select, MenuItem, styled } from '@mui/material';
import { ExpandMore, Check } from '@mui/icons-material';
import { Colors } from 'library';

interface RSelectOption {
    key: string | number;
    value: string;
}

interface RSelectProps {
    value: string | number;
    onChange: (value: string) => void;
    options: RSelectOption[];
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
}

export function RSelect({ value, onChange, options, placeholder, disabled, fullWidth }: RSelectProps) {
    return (
        <StyledSelect
            value={value}
            onChange={(e) => onChange(e.target.value as string)}
            disabled={disabled}
            fullWidth={fullWidth}
            displayEmpty={!!placeholder}
            IconComponent={StyledExpandMore}
            MenuProps={{
                PaperProps: {
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                    },
                },
            }}
            renderValue={(selected) => {
                if (placeholder && !selected) return <Placeholder>{placeholder}</Placeholder>;
                return options.find((o) => String(o.key) === String(selected))?.value || '';
            }}
        >
            {options.map((opt) => (
                <StyledMenuItem key={opt.key} value={opt.key} selected={String(opt.key) === String(value)}>
                    {String(opt.key) === String(value) && <Check sx={{ fontSize: 18, marginRight: '8px' }} />}
                    {opt.value}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}

const StyledExpandMore = styled(ExpandMore)({
    fontSize: 20,
    color: Colors.mediumGrey,
});

const StyledSelect = styled(Select)({
    fontSize: 14,
    color: Colors.darkNavy,
    backgroundColor: Colors.faintGrey,
    borderRadius: 10,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: Colors.lightGrey,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: Colors.lightGrey,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: Colors.mediumGrey,
        borderWidth: 1,
    },
    '&.Mui-disabled': {
        backgroundColor: Colors.lightGrey,
        color: Colors.mediumGrey,
    },
    '& .MuiSelect-select': {
        padding: '10px 14px',
    },
});

const StyledMenuItem = styled(MenuItem)({
    fontSize: 14,
    padding: '7px 14px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: Colors.darkNavy,
    },
    '&.Mui-selected:hover': {
        backgroundColor: Colors.turquoise,
        color: Colors.white,
    },
    '&:hover': {
        backgroundColor: Colors.turquoise,
        color: Colors.white,
    },
});

const Placeholder = styled('span')({
    color: Colors.disabledGrey,
});
